import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo, CtaButton, MonoLabel } from "../components/kit";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = mode === "register" ? await register(name, email, password) : await login(email, password);
      const returnTo = params.get("returnTo");
      const safeReturn = returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//") ? returnTo : null;
      navigate(user.onboarding_completed ? safeReturn || "/app" : "/onboarding");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen dot-grid flex flex-col" data-testid="auth-page">
      <div className="max-w-6xl w-full mx-auto px-6 py-6">
        <Link to="/" data-testid="auth-logo-link">
          <Logo className="h-5 sm:h-6" />
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-24">
        <div className="w-full max-w-md">
          <div className="card-soft p-8 sm:p-10 fade-up">
            <MonoLabel>{mode === "register" ? "CREATE ACCOUNT" : "WELCOME BACK"}</MonoLabel>
            <h1 className="mt-5 text-2xl sm:text-3xl leading-tight">
              <span className="font-serif-it block">{mode === "register" ? "Your budget deserves" : "Back to"}</span>
              <span className="font-extrabold tracking-[-0.02em] block">{mode === "register" ? "a direction" : "your direction"}</span>
            </h1>
            <button
              type="button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/app";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="mt-7 w-full flex items-center justify-center gap-2.5 rounded-full border border-[#EAE5DB] bg-white py-3 text-[14px] font-semibold text-[#181621] transition-[border-color,transform] hover:border-[#181621] active:scale-[0.98]"
              data-testid="google-auth-btn"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              </svg>
              Continue with Google
            </button>
            <div className="flex items-center gap-3 mt-6">
              <span className="flex-1 h-px bg-[#EAE5DB]" />
              <span className="font-mono-j text-[9px] tracking-[0.14em] text-[#9A9591] uppercase">or with email</span>
              <span className="flex-1 h-px bg-[#EAE5DB]" />
            </div>
            <form onSubmit={submit} className="mt-5 space-y-4">
              {mode === "register" && (
                <div>
                  <label className="font-mono-j text-[10px] tracking-[0.12em] text-[#54505B] uppercase block mb-1.5">Name</label>
                  <input
                    className="field-input"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="auth-name-input"
                  />
                </div>
              )}
              <div>
                <label className="font-mono-j text-[10px] tracking-[0.12em] text-[#54505B] uppercase block mb-1.5">Email</label>
                <input
                  className="field-input"
                  type="email"
                  placeholder="you@business.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="auth-email-input"
                />
              </div>
              <div>
                <label className="font-mono-j text-[10px] tracking-[0.12em] text-[#54505B] uppercase block mb-1.5">Password</label>
                <input
                  className="field-input"
                  type="password"
                  placeholder={mode === "register" ? "At least 6 characters" : "Your password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="auth-password-input"
                />
                {mode === "login" && (
                  <Link to="/forgot-password" className="block text-right text-[12.5px] text-[#54505B] underline underline-offset-2 mt-1.5" data-testid="forgot-password-link">
                    Forgot your password?
                  </Link>
                )}
              </div>
              {error && (
                <p className="text-[13px] text-[#B4443C] bg-[#FBE9E7] rounded-lg px-3.5 py-2.5" data-testid="auth-error-message">
                  {error}
                </p>
              )}
              <CtaButton type="submit" className="w-full mt-2" disabled={busy} data-testid="auth-submit-btn">
                {busy ? "One moment…" : mode === "register" ? "Create my account" : "Log in"}
              </CtaButton>
            </form>
            <p className="text-[13.5px] text-[#54505B] mt-6 text-center">
              {mode === "register" ? "Already have an account?" : "New to Agenzee?"}{" "}
              <button
                type="button"
                className="font-semibold text-[#181621] underline underline-offset-2"
                onClick={() => {
                  setMode(mode === "register" ? "login" : "register");
                  setError("");
                }}
                data-testid="auth-toggle-mode-btn"
              >
                {mode === "register" ? "Log in" : "Sign up"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
